'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, ArrowLeft, ArrowRight, Check, ChevronDown } from 'lucide-react';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.myfitcrm.ru/v1';

const TOTAL_STEPS = 6;

/* ─── Types ─── */

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

interface WizardProps {
  onClose: () => void;
}

/* ─── Helpers ─── */

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  let d = digits;
  if (d.startsWith('8') && d.length > 1) d = '7' + d.slice(1);
  if (d.startsWith('7')) d = d.slice(1);
  if (d.length === 0) return '+7 ';
  let out = '+7 (';
  out += d.slice(0, 3);
  if (d.length >= 3) out += ') ';
  else return out;
  out += d.slice(3, 6);
  if (d.length >= 6) out += '-';
  else return out;
  out += d.slice(6, 8);
  if (d.length >= 8) out += '-';
  else return out;
  out += d.slice(8, 10);
  return out;
}

function phoneDigits(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

function isPhoneValid(formatted: string): boolean {
  return phoneDigits(formatted).length === 11;
}

function isEmailValid(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const RUSSIAN_CITIES = [
  'Москва',
  'Санкт-Петербург',
  'Новосибирск',
  'Екатеринбург',
  'Казань',
  'Нижний Новгород',
  'Челябинск',
  'Самара',
  'Омск',
  'Ростов-на-Дону',
  'Уфа',
  'Красноярск',
  'Воронеж',
  'Пермь',
  'Волгоград',
  'Краснодар',
  'Саратов',
  'Тюмень',
  'Тольятти',
  'Ижевск',
  'Барнаул',
  'Иркутск',
  'Ульяновск',
  'Хабаровск',
  'Ярославль',
  'Владивосток',
  'Махачкала',
  'Томск',
  'Оренбург',
  'Кемерово',
  'Новокузнецк',
  'Рязань',
  'Астрахань',
  'Набережные Челны',
  'Пенза',
  'Липецк',
  'Тула',
  'Киров',
  'Чебоксары',
  'Калининград',
  'Сочи',
  'Брянск',
  'Курск',
  'Тверь',
  'Белгород',
  'Сургут',
  'Владимир',
  'Архангельск',
  'Смоленск',
  'Калуга',
];

const BUSINESS_TYPES = [
  'Фитнес-клуб',
  'Студия',
  'Единоборства',
  'Бассейн',
  'Танцы',
  'Йога',
  'Растяжка',
  'EMS',
  'Другое',
];

const EMPLOYEE_COUNTS = ['1', '2-4', '5-7', '8-14', '15+'];

const POSITIONS = ['Владелец', 'Управляющий', 'Администратор', 'Тренер', 'Маркетолог', 'Бухгалтер'];

const BRANCHES = ['Один', 'Несколько', 'Я — частный специалист'];

const REFERRAL_SOURCES = [
  'Рекомендация',
  'Реклама в Интернете',
  'Конференция',
  'Знаю давно',
  'Не помню',
];

const INTERESTS = [
  'CRM и воронка продаж',
  'Расписание занятий',
  'Мобильное приложение',
  'SIP-телефония',
  'Аналитика и отчёты',
  'Push-уведомления',
  'Онлайн-запись',
  'Абонементы и продажи',
  'Расчёт зарплаты',
  'API и Webhooks',
];

/* ─── Chip Select Component ─── */

function ChipSelect({
  options,
  value,
  onChange,
  multi = false,
}: {
  options: string[];
  value: string | string[];
  onChange: (val: string | string[]) => void;
  multi?: boolean;
}) {
  const selected = Array.isArray(value) ? value : [value];

  function toggle(opt: string) {
    if (multi) {
      const arr = selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt];
      onChange(arr);
    } else {
      onChange(opt === value ? '' : opt);
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isActive = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              isActive
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Dropdown Component ─── */

function Dropdown({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: string[];
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm text-left focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || placeholder}</span>
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-48 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                value === opt ? 'text-primary font-medium' : 'text-gray-700'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── City Autocomplete Component ─── */

function CityInput({ value, onChange }: { value: string; onChange: (val: string) => void }) {
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    if (!value || value.length < 2) return [];
    const lower = value.toLowerCase();
    return RUSSIAN_CITIES.filter((c) => c.toLowerCase().includes(lower)).slice(0, 6);
  }, [value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        placeholder="Начните вводить название города"
        className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
      />
      {focused && suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((city) => (
            <button
              key={city}
              type="button"
              onClick={() => {
                onChange(city);
                setFocused(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {city}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main Wizard ─── */

export default function RegistrationWizard({ onClose }: WizardProps) {
  // Steps state
  const [step, setStep] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  // API state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [leadId, setLeadId] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [completed, setCompleted] = useState(false);
  const [repeatRequest, setRepeatRequest] = useState(false);

  // UTM
  const [utm, setUtm] = useState<UtmParams>({});

  // Honeypot
  const [honeypot, setHoneypot] = useState('');

  // Step 1: Registration
  const [phone, setPhone] = useState('+7 ');
  const [email, setEmail] = useState('');
  const [consentData, setConsentData] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  // Step 2: SMS
  const [smsCode, setSmsCode] = useState('');
  const [timer, setTimer] = useState(60);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step 3: About you
  const [name, setName] = useState('');
  const [branches, setBranches] = useState('');
  const [brandName, setBrandName] = useState('');
  const [position, setPosition] = useState('');

  // Step 4: Business
  const [businessType, setBusinessType] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [selfService, setSelfService] = useState('');

  // Step 5: Where from
  const [city, setCity] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  // Step 6: Interests
  const [interests, setInterests] = useState<string[]>([]);

  // Collect UTM on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const u: UtmParams = {};
    for (const key of [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
    ] as const) {
      const val = params.get(key);
      if (val) u[key] = val;
    }
    setUtm(u);
  }, []);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // SMS timer
  useEffect(() => {
    if (step === 2 && timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((t) => {
          if (t <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [step, timer]);

  // Auto-submit SMS code when 4 digits entered
  useEffect(() => {
    if (smsCode.length === 4 && step === 2) {
      handleVerifySms();
    }
    // eslint-disable-next-line
  }, [smsCode]);

  // Escape key to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* ─── Navigation ─── */

  const goTo = useCallback((target: number, dir: 'forward' | 'back') => {
    setDirection(dir);
    setTransitioning(true);
    setTimeout(() => {
      setStep(target);
      setError('');
      setTransitioning(false);
    }, 200);
  }, []);

  const goNext = useCallback(() => goTo(step + 1, 'forward'), [goTo, step]);
  const goBack = useCallback(() => goTo(step - 1, 'back'), [goTo, step]);

  /* ─── API calls ─── */

  async function handleStartRegistration() {
    if (honeypot) {
      setCompleted(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/registration/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phoneDigits(phone),
          email,
          consentDataProcessing: consentData,
          consentMarketing,
          ...utm,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Ошибка регистрации. Попробуйте позже.');
        return;
      }
      if (data.status === 'repeat_request') {
        setRepeatRequest(true);
        return;
      }
      setLeadId(data.id || data.leadId);
      setTimer(60);
      goNext();
    } catch {
      setError('Ошибка соединения. Проверьте интернет и попробуйте снова.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifySms() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/registration/verify-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId, code: smsCode }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Неверный код. Попробуйте ещё раз.');
        setSmsCode('');
        return;
      }
      setTempToken(data.tempToken || data.token || '');
      goNext();
    } catch {
      setError('Ошибка соединения. Попробуйте снова.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendSms() {
    setError('');
    try {
      await fetch(`${API_URL}/registration/${leadId}/resend-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      setTimer(60);
    } catch {
      setError('Не удалось отправить код повторно.');
    }
  }

  async function handleFinish() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/registration/${leadId}/questionnaire`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-temp-token': tempToken,
        },
        body: JSON.stringify({
          name,
          branchCount: branches,
          brandName,
          position,
          businessType,
          employeeCount,
          selfService: selfService === 'Да',
          city,
          referralSource,
          promoCode: promoCode || undefined,
          interestedFeatures: interests,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Ошибка сохранения. Попробуйте позже.');
        return;
      }
      setCompleted(true);
    } catch {
      setError('Ошибка соединения. Попробуйте снова.');
    } finally {
      setLoading(false);
    }
  }

  /* ─── Step validity ─── */

  function isStepValid(): boolean {
    switch (step) {
      case 1:
        return isPhoneValid(phone) && isEmailValid(email) && consentData;
      case 2:
        return smsCode.length === 4;
      case 3:
        return name.trim().length > 0;
      case 4:
        return businessType.length > 0;
      case 5:
        return true;
      case 6:
        return interests.length > 0;
      default:
        return false;
    }
  }

  function handleStepAction() {
    switch (step) {
      case 1:
        handleStartRegistration();
        break;
      case 2:
        handleVerifySms();
        break;
      case 3:
      case 4:
      case 5:
        goNext();
        break;
      case 6:
        handleFinish();
        break;
    }
  }

  /* ─── Masked phone display ─── */

  const maskedPhone = useMemo(() => {
    const d = phoneDigits(phone);
    if (d.length < 11) return phone;
    return `+7 ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
  }, [phone]);

  /* ─── Step titles ─── */

  const stepTitles = [
    'Регистрация',
    'Подтверждение телефона',
    'Давайте знакомиться!',
    'Пара вопросов о вашем бизнесе',
    'Откуда вы?',
    'Что вас интересует?',
  ];

  /* ─── Render ─── */

  if (repeatRequest) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="text-center px-6 max-w-md animate-fade-in-up">
          <div className="mx-auto w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center">
              <span className="text-white text-2xl">!</span>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Заявка уже оставлена</h2>
          <p className="mt-3 text-gray-500 leading-relaxed">
            Вы уже оставляли заявку на триал, наш менеджер свяжется с Вами, если у вас остались
            вопросы
          </p>
          <button
            onClick={onClose}
            className="mt-8 inline-flex items-center justify-center rounded-xl bg-primary px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary-dark transition-all"
          >
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="text-center px-6 max-w-md animate-fade-in-up">
          <div className="mx-auto w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center animate-[scale-in_0.3s_ease-out]">
              <span className="text-white text-2xl">✉</span>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Проверьте вашу почту!</h2>
          <p className="mt-3 text-gray-500 leading-relaxed">
            Мы отправили ссылку для активации кабинета на{' '}
            <strong className="text-gray-700">{email}</strong>. Перейдите по ссылке в письме, чтобы
            начать работу.
          </p>
          <p className="mt-2 text-sm text-gray-400">Не нашли письмо? Проверьте папку «Спам»</p>
          <button
            onClick={onClose}
            className="mt-8 inline-flex items-center justify-center rounded-xl bg-primary px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary-dark transition-all"
          >
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-gray-100">
        <div className="mx-auto max-w-lg px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-white font-bold text-sm">MF</span>
            </div>
            <span className="text-lg font-bold text-gray-900">
              My<span className="text-primary">Fit</span>CRM
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Закрыть"
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mx-auto max-w-lg px-4 sm:px-6 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">
              Шаг {step} из {TOTAL_STEPS}
            </span>
            <span className="text-sm font-medium text-gray-400">{stepTitles[step - 1]}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div
          className={`mx-auto max-w-lg px-4 sm:px-6 py-8 transition-all duration-200 ${
            transitioning
              ? direction === 'forward'
                ? 'opacity-0 translate-x-4'
                : 'opacity-0 -translate-x-4'
              : 'opacity-100 translate-x-0'
          }`}
        >
          {/* Honeypot */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            className="absolute -left-[9999px] opacity-0 h-0 w-0"
            aria-hidden="true"
          />

          {error && (
            <div className="mb-6 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-xl">{error}</div>
          )}

          {/* ─── Step 1: Registration ─── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Регистрация</h2>
                <p className="mt-1 text-gray-500">
                  Создайте аккаунт, чтобы начать работу с MyFitCRM
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Телефон</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="+7 (___) ___-__-__"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={consentData}
                    onChange={(e) => setConsentData(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20 accent-primary"
                  />
                  <span className="text-sm text-gray-600 leading-snug">
                    Я даю согласие ИП Лопашов Е.О. на{' '}
                    <a
                      href="/legal/data-processing"
                      target="_blank"
                      className="text-primary underline hover:text-primary-dark"
                    >
                      обработку персональных данных
                    </a>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={consentMarketing}
                    onChange={(e) => setConsentMarketing(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20 accent-primary"
                  />
                  <span className="text-sm text-gray-600 leading-snug">
                    На получение сообщений и информационно-рекламной рассылки
                  </span>
                </label>
              </div>

              <p className="text-xs text-gray-400 leading-relaxed pt-1">
                Нажимая на кнопку, я соглашаюсь с условиями{' '}
                <a href="/legal/terms" target="_blank" className="underline hover:text-gray-600">
                  Пользовательского соглашения
                </a>{' '}
                и{' '}
                <a href="/legal/privacy" target="_blank" className="underline hover:text-gray-600">
                  Политики конфиденциальности
                </a>
              </p>
            </div>
          )}

          {/* ─── Step 2: SMS Verification ─── */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Подтвердите номер телефона</h2>
                <p className="mt-1 text-gray-500">
                  Мы позвоним на {maskedPhone} — введите последние 4 цифры входящего номера
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Код из звонка
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="____"
                  autoFocus
                  className="w-full rounded-xl border border-gray-200 px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] text-gray-900 placeholder:text-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              <div className="text-center text-sm">
                {timer > 0 ? (
                  <span className="text-gray-400">Повторный звонок через {timer} сек</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendSms}
                    className="text-primary font-medium hover:text-primary-dark transition-colors"
                  >
                    Позвонить повторно
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ─── Step 3: About You ─── */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Давайте знакомиться!</h2>
                <p className="mt-1 text-gray-500">
                  Расскажите о себе и своей компании. Это займет до 30 секунд.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Ваше имя</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Как вас зовут?"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Сколько у компании филиалов?
                </label>
                <ChipSelect
                  options={BRANCHES}
                  value={branches}
                  onChange={(v) => setBranches(v as string)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Наименование бренда
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Название вашего клуба или студии"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Должность</label>
                <Dropdown
                  options={POSITIONS}
                  value={position}
                  onChange={setPosition}
                  placeholder="Выберите должность"
                />
              </div>
            </div>
          )}

          {/* ─── Step 4: Business ─── */}
          {step === 4 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Пара вопросов о вашем бизнесе</h2>
                <p className="mt-1 text-gray-500">Это поможет нам настроить систему под вас</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Тип бизнеса</label>
                <ChipSelect
                  options={BUSINESS_TYPES}
                  value={businessType}
                  onChange={(v) => setBusinessType(v as string)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Кол-во сотрудников
                </label>
                <ChipSelect
                  options={EMPLOYEE_COUNTS}
                  value={employeeCount}
                  onChange={(v) => setEmployeeCount(v as string)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Оказываете услуги самостоятельно?
                </label>
                <ChipSelect
                  options={['Да', 'Нет']}
                  value={selfService}
                  onChange={(v) => setSelfService(v as string)}
                />
              </div>
            </div>
          )}

          {/* ─── Step 5: Where From ─── */}
          {step === 5 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Откуда вы?</h2>
                <p className="mt-1 text-gray-500">Расскажите, откуда вы узнали о нас</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Город</label>
                <CityInput value={city} onChange={setCity} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Как вы о нас узнали?
                </label>
                <ChipSelect
                  options={REFERRAL_SOURCES}
                  value={referralSource}
                  onChange={(v) => setReferralSource(v as string)}
                />
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowPromo(!showPromo)}
                  className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${showPromo ? 'rotate-180' : ''}`}
                  />
                  У меня есть промокод
                </button>
                {showPromo && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Введите промокод"
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Step 6: Interests ─── */}
          {step === 6 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Что вас интересует?</h2>
                <p className="mt-1 text-gray-500">
                  Инструменты MyFitCRM позволяют решать множество задач. Выберите те функции,
                  которые вам интересны.
                </p>
              </div>

              <ChipSelect
                options={INTERESTS}
                value={interests}
                onChange={(v) => setInterests(v as string[])}
                multi
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer navigation */}
      <div className="shrink-0 border-t border-gray-100">
        <div className="mx-auto max-w-lg px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          {step > 1 && step <= TOTAL_STEPS ? (
            <button
              type="button"
              onClick={goBack}
              disabled={loading || step === 2}
              className="flex items-center gap-2 rounded-xl border border-gray-200 px-5 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeft size={16} />
              Назад
            </button>
          ) : (
            <div />
          )}

          <button
            type="button"
            onClick={handleStepAction}
            disabled={!isStepValid() || loading}
            className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary-dark transition-all disabled:opacity-40 disabled:cursor-not-allowed min-w-[180px] justify-center"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Загрузка...
              </span>
            ) : step === 1 ? (
              'Зарегистрироваться'
            ) : step === 6 ? (
              <>
                Завершить регистрацию
                <Check size={16} />
              </>
            ) : step === 2 ? (
              'Подтвердить'
            ) : (
              <>
                Далее
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
